import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Button, 
    Input, 
    Modal, 
    message, 
    Space, 
    Card,
    Popconfirm,
    Tag,
    Tooltip,
    Form,
    Select
} from 'antd';
import { 
    SearchOutlined, 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined
} from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import '../../assets/styles/dashboard.css';

const { Option } = Select;

const PharmacyCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [form] = Form.useForm();

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'pharmacy_categories';
            const conditions = {};
            const orderBy = 'id ASC';
            const search = searchText?.trim() ? { 
                name: searchText.trim(),
                description: searchText.trim()
            } : {};
            const paginationData = {
                page: pagination.current,
                limit: pagination.pageSize
            };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);
            
            if (response.success) {
                const categoryData = response.data || [];
                setCategories(categoryData);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || categoryData.length
                }));
            } else {
                console.error('API returned error:', response);
                message.error('Failed to fetch categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            message.error('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, searchText]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Debounced search to improve performance
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    handleSearch(value);
                }, 300);
            };
        })(),
        []
    );

    const handleTableChange = (paginationInfo) => {
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize
        }));
    };

    const showModal = () => {
        setShowAddModal(true);
        form.resetFields();
    };

    const closeModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditData(null);
        form.resetFields();
    };

    const handleShowEditModal = (record) => {
        setEditData(record);
        setShowEditModal(true);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            status: record.status
        });
    };

    const handleDelete = async (id) => {
        try {
            const tableSchema = '';
            const tableName = 'pharmacy_categories';
            const conditions = { id: id };

            const response = await oneKlicks.delete(tableSchema, tableName, conditions);
            
            if (response.success) {
                message.success('Category deleted successfully');
                fetchCategories();
            } else {
                message.error(response.message || 'Failed to delete category');
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            message.error('Failed to delete category');
        }
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitLoading(true);
            const tableSchema = '';
            const tableName = 'pharmacy_categories';
            
            // Check for duplicate names (excluding current record if editing)
            const existingCategory = categories.find(cat => 
                cat.name.toLowerCase() === values.name.toLowerCase() && 
                (!editData || cat.id !== editData.id)
            );
            
            if (existingCategory) {
                message.error('A category with this name already exists');
                return;
            }
            
            if (showEditModal && editData) {
                // Update existing category
                const conditions = { id: editData.id };
                const response = await oneKlicks.update(tableSchema, tableName, conditions, values);
                
                if (response.success) {
                    message.success('Category updated successfully');
                    closeModal();
                    fetchCategories();
                } else {
                    message.error(response.message || 'Failed to update category');
                }
            } else {
                // Create new category
                const response = await oneKlicks.create(tableSchema, tableName, values);
                
                if (response.success) {
                    message.success('Category created successfully');
                    closeModal();
                    fetchCategories();
                } else {
                    message.error(response.message || 'Failed to create category');
                }
            }
        } catch (error) {
            console.error('Error saving category:', error);
            message.error('Failed to save category');
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            render: (id) => <span className="text-muted">#{id}</span>
        },
        {
            title: 'Category Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name) => <strong>{name}</strong>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 300,
            render: (description) => (
                <span style={{ color: '#666' }}>
                    {description || 'No description available'}
                </span>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            onClick={() => handleShowEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Are you sure you want to delete this category?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <div className="">
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                    <Card className="shadow-sm" style={{ width: '100%' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Input
                                placeholder="Search categories by name or description"
                                prefix={<SearchOutlined />}
                                style={{ width: 350 }}
                                onChange={(e) => debouncedSearch(e.target.value)}
                                allowClear
                            />
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />} 
                                onClick={showModal}
                            >
                                Add Category
                            </Button>
                        </div>
                        
                        <Table
                            columns={columns}
                            dataSource={categories}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => 
                                    `${range[0]}-${range[1]} of ${total} categories`
                            }}
                            onChange={handleTableChange}
                            tableLayout="fixed"
                            style={{ width: '100%' }}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                                        <div style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}>
                                            📂
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                                            No categories found
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#999' }}>
                                            {categories.length === 0 ? 
                                                'Get started by adding your first category' : 
                                                'Try adjusting your search criteria'
                                            }
                                        </div>
                                        {categories.length === 0 && (
                                            <Button 
                                                type="primary" 
                                                icon={<PlusOutlined />}
                                                onClick={showModal}
                                                style={{ marginTop: '16px' }}
                                            >
                                                Add First Category
                                            </Button>
                                        )}
                                    </div>
                                )
                            }}
                        />
                    </Card>
                </div>
            </div>

            {/* Add/Edit Category Modal */}
            <Modal
                title={showEditModal ? 'Edit Category' : 'Add New Category'}
                open={showAddModal || showEditModal}
                onCancel={closeModal}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        status: 'active'
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[{ required: true, message: 'Please enter category name' }]}
                    >
                        <Input placeholder="Enter category name" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea
                            placeholder="Enter category description"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select status' }]}
                    >
                        <Select placeholder="Select status">
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </Form.Item>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading} disabled={submitLoading}>
                                {showEditModal ? 'Update Category' : 'Add Category'}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </section>
    );
};

export default PharmacyCategories; 
